import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import LayoutUserHeader from '../components/LayoutUserHeader';
import LayoutUserFooter from '../components/LayoutUserFooter';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ProductList = () => {
  const [mappings, setMappings] = useState([]);
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const [productName, setProductName] = useState('');
  const [hsnCode, setHsnCode] = useState('');
  const [unit, setUnit] = useState('');
  const [gst, setGst] = useState('');
  const [unitsList, setUnitsList] = useState([]);
  const [gstList, setGstList] = useState([]);
  const GST_NUMBER = localStorage.getItem('gstNumber');

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

const res = await axios.get(
  `${BASE_URL}/ProductUnitHsnMapping/GetProductDetailsBasedOnGST`,
  {
    params: { gstNumber: GST_NUMBER },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

      const data = res.data.data || [];
      const formatted = data.map((item, index) => ({
        id: item.id || index,
        ProductName: item.ProductName || 'N/A',
        HSNCode: item.HSNCode || 'N/A',
        Unit: item.UnitId?.UnitName || 'N/A',
        UnitId: item.UnitId?._id || '',
        GSTPercentage: item.GstPercentageId?.PercentageValue || '0',
        GstPercentageId: item.GstPercentageId?._id || '',
        gstNumber: item.gstNumber
      }));

      setMappings(formatted);
      setFilteredMappings(formatted);
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
     const token = localStorage.getItem("token");

const res = await axios.get(`${BASE_URL}/Unit/GetUnitBasedOnGST`, {
  params: { gstNumber: GST_NUMBER },
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

      setUnitsList(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch units:', err);
    }
  };

  const fetchGSTs = async () => {
    try {
      const token = localStorage.getItem("token");

const res = await axios.get(`${BASE_URL}/GSTPercentage/GetAllGsts`, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});
      console.log(res);
      setGstList(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch GST percentages:', err);
    }
  };

  useEffect(() => {
    fetchMappings();
    fetchUnits();
    fetchGSTs();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    const filtered = mappings.filter(item =>
      item.ProductName.toLowerCase().includes(value) ||
      item.HSNCode.toLowerCase().includes(value) ||
      item.Unit.toLowerCase().includes(value) ||
      String(item.GSTPercentage).toLowerCase().includes(value)
    );
    setFilteredMappings(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure to delete this mapping?')) return;
    try {
      const token = localStorage.getItem("token");

const res = await axios.delete(
  `${BASE_URL}/ProductUnitHsnMapping/DeleteMapping/${id}`,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
      fetchMappings();
    } catch (err) {
      alert('Failed to delete mapping');
    }
  };

  const handleEdit = (row) => {
    setSelected(row);
    setProductName(row.ProductName);
    setHsnCode(row.HSNCode);
    setUnit(row.UnitId);
    setGst(row.GstPercentageId);
    setOpen(true);
  };

  const handleAdd = () => {
    setSelected(null);
    setProductName('');
    setHsnCode('');
    setUnit('');
    setGst('');
    setOpen(true);
  };

  const handleSave = async () => {
    if (!productName || !hsnCode || !unit || !gst) {
      alert('Please fill in all fields');
      return;
    }

    const payload = {
      ProductName: productName.trim(),
      HSNCode: hsnCode.trim(),
      UnitId: unit,
      GstPercentageId: gst,
      gstNumber: GST_NUMBER
    };

    try {
      if (selected) {
        const token = localStorage.getItem("token");

const res = await axios.put(
  `${BASE_URL}/ProductUnitHsnMapping/UpdateMapping/${selected.id}`,
  payload,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
      } else {
        const token = localStorage.getItem("token");

const res = await axios.post(
  `${BASE_URL}/ProductUnitHsnMapping/AddMapping`,
  payload,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
      }
      setOpen(false);
      fetchMappings();
    } catch (err) {
      console.error('Save failed:', err);
      alert(err.response?.data?.message || 'Failed to save mapping');
    }
  };

  const columns = [
    { field: 'ProductName', headerName: 'Product Name', flex: 1 },
    { field: 'HSNCode', headerName: 'HSN Code', flex: 1 },
    { field: 'Unit', headerName: 'Unit', flex: 1 },
    { field: 'GSTPercentage', headerName: 'GST %', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Button variant="outlined" color="primary" size="small" onClick={() => handleEdit(params.row)}>
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDelete(params.row.id)}
            style={{ marginLeft: 8 }}
          >
            Delete
          </Button>
        </>
      )
    }
  ];

  return (
    <>
      <LayoutUserHeader />
      <Box p={2}>
        <Typography variant="h5" gutterBottom>
          Product Mapping List
        </Typography>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <TextField
            label="Search"
            value={search}
            onChange={handleSearch}
            placeholder="Search by product name, HSN, unit or GST %"
            fullWidth
          />
          <Button variant="contained" color="primary" onClick={handleAdd} style={{ marginLeft: 10 }}>
            Add Mapping
          </Button>
        </Box>
        <DataGrid
          autoHeight
          rows={filteredMappings}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10]}
          loading={loading}
          disableSelectionOnClick
          sortingOrder={['asc', 'desc']}
        />

        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{selected ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Product Name"
              fullWidth
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
            {/* <TextField
              margin="dense"
              label="HSN Code"
              fullWidth
              value={hsnCode}
              onChange={(e) => setHsnCode(e.target.value)}
            /> */}
            <TextField
  margin="dense"
  label="HSN Code"
  fullWidth
  value={hsnCode}
  onChange={(e) => {
    const value = e.target.value;

    // Allow only digits and max 8 characters
    if (/^\d{0,8}$/.test(value)) {
      setHsnCode(value);
    }
  }}
  inputProps={{
    maxLength: 8,
    inputMode: "numeric", // For mobile keyboard
  }}
/>
            <TextField
              select
              margin="dense"
              label="Unit"
              fullWidth
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            >
              {unitsList.map((u) => (
                <MenuItem key={u._id || u.id} value={u._id || u.id}>
                  {u.UnitName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              margin="dense"
              label="GST Percentage"
              fullWidth
              value={gst}
              onChange={(e) => setGst(e.target.value)}
            >
              {gstList.map((g) => (
                <MenuItem key={g._id || g.id} value={g._id || g.id}>
                  {g.PercentageValue}%
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>
              {selected ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
       <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <LayoutUserFooter />
    </>
  );
};

export default ProductList;
